import type { CraftingComponent, MaterialDefinition } from "@/lib/crafting/types";
import { assetUrl } from "@/lib/bungie/assets";
import { ResourceLine } from "./ResourceLine";

export interface PerkTooltipProps {
  component: CraftingComponent;
  materials: Record<string, MaterialDefinition>;
  /** statHash -> name, used to label a plug's stat modifiers. */
  statNames?: Record<string, string>;
  currentLevel: number;
  equipped?: boolean;
  /**
   * Whether this is the component chosen for the slot, or just the one under
   * the pointer. Without this the panel silently swaps between the two and it
   * is not obvious which you are looking at.
   */
  state?: "selected" | "preview";
  /** Rendered into an aria-live region by the parent when it is the live target. */
  id?: string;
}

/**
 * The perk detail panel.
 *
 * Follows the game's tooltip anatomy (header, enhanced subtitle, description,
 * info block) and then adds the two things a planner needs that the game hides:
 * the required weapon level and the resource cost.
 */
export function PerkTooltip({
  component,
  materials,
  statNames,
  currentLevel,
  equipped,
  state = "selected",
  id,
}: PerkTooltipProps) {
  const locked = component.requiredLevel > currentLevel;
  const icon = assetUrl(component.icon);
  const statModifiers = Object.entries(component.stats ?? {})
    .map(([hash, value]) => ({ hash, value, name: statNames?.[hash] }))
    .filter((s) => s.name && s.value !== 0);

  return (
    <div
      id={id}
      className="w-[22rem] max-w-full border border-line-dim bg-panel/95 backdrop-blur-sm"
    >
      <header className="border-b border-line-dim bg-white/6 px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <h3 className="type-tooltip text-lg leading-tight">{component.name}</h3>
          <span
            className={[
              "label-caps mt-0.5 shrink-0 border px-1.5 py-0.5 text-[0.6rem] leading-none",
              state === "selected"
                ? "border-line-dim text-ink-2"
                : "border-enhanced/50 text-enhanced",
            ].join(" ")}
          >
            {state === "selected" ? "In slot" : "Preview"}
          </span>
        </div>
        <p className="mt-1 text-sm text-ink-2">
          {component.enhanced && <span aria-hidden className="mr-1 text-enhanced">↑</span>}
          <span className={component.enhanced ? "text-enhanced" : undefined}>
            {component.itemTypeDisplayName ?? (component.enhanced ? "Enhanced Trait" : "Trait")}
          </span>
          {equipped && <span className="ml-2 text-ok">• Currently equipped</span>}
        </p>
      </header>

      {component.description && (
        <div
          className={[
            "flex gap-3 px-4 py-3",
            component.enhanced ? "border-l-2 border-l-enhanced" : "",
          ].join(" ")}
        >
          {icon && (
            <img
              src={icon}
              alt=""
              aria-hidden
              className="mt-0.5 h-9 w-9 shrink-0 rounded-full ring-1 ring-white/20"
            />
          )}
          <p className="text-sm leading-relaxed text-ink-2">{component.description}</p>
        </div>
      )}

      {/* Several plugs share a display name (every "Enhanced Intrinsic", for
          one) and differ only in what they boost — so this is load-bearing. */}
      {statModifiers.length > 0 && (
        <ul className="border-t border-line-dim px-4 py-3 text-sm">
          {statModifiers.map((stat) => (
            <li key={stat.hash} className="flex items-baseline justify-between gap-4">
              <span className="text-ink-2">{stat.name}</span>
              <span
                className={`font-semibold tabular-nums ${stat.value > 0 ? "text-ok" : "text-short"}`}
              >
                {stat.value > 0 ? "+" : ""}
                {stat.value}
              </span>
            </li>
          ))}
        </ul>
      )}

      <dl className="space-y-2 border-t border-line-dim px-4 py-3 text-sm">
        <div className="flex items-baseline justify-between gap-4">
          <dt className="text-ink-3">Required weapon level</dt>
          <dd className={locked ? "font-semibold text-level" : "font-semibold text-ok"}>
            {component.requiredLevel === 0 ? "None" : component.requiredLevel}
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-4">
          <dt className="text-ink-3">Availability</dt>
          <dd className={locked ? "text-level" : "text-ok"}>
            {locked ? `Locked until level ${component.requiredLevel}` : "Available now"}
          </dd>
        </div>
      </dl>

      <div className="border-t border-line-dim px-4 py-3">
        <h4 className="label-caps mb-2 text-xs text-ink-3">Cost to insert</h4>
        {component.costs.length === 0 ? (
          <p className="text-sm text-ink-3">No resource cost.</p>
        ) : (
          <ul className="space-y-1">
            {component.costs.map((cost) => (
              <li key={cost.itemHash}>
                <ResourceLine amount={cost} materials={materials} />
              </li>
            ))}
          </ul>
        )}
      </div>

      {!component.currentlyCanRoll && (
        <p className="border-t border-line-dim px-4 py-2 text-xs text-ink-3">
          <span aria-hidden className="mr-1">ⓘ</span>
          No longer in the current loot pool.
        </p>
      )}
    </div>
  );
}
